import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';

import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { ApiError } from '../../../common/classes/ApiError';
import { InstructorProfileData } from '../../../common/interfaces/instructorProfile';
import { selectUser } from '../../../common/store/slices/userSlice';
import { DATE_PICKER_VALUE_FORMAT } from '../../../common/utils/cloudscapeHelpers';
import { AppDispatch, GlobalState } from '../../../main/store';
import {
    ActivityStatus,
    ActivityLMSType,
} from '../../components/Activity/Common/Common';
import { V_ILT_TYPE } from '../../constants';
import {
    AciDeliverySessionType,
    ACTIVITY_GROUP_NAME_EMBEDDED_VALUE_DELIMITER,
} from '../../constants/aci';
import { DeliveryInstructor, DeliverySession } from '../../interfaces/activity';
import {
    ActivityGroupChildActivity,
    ActivityGroupData,
} from '../../interfaces/activityGroup';
import {
    getActivityGroupData,
    getActivityGroupOnly,
} from '../../services/activity-service';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(customParseFormat);

const DEFAULT_CLASS_DAYS = 90;

const guessedTimezone = dayjs.tz.guess();
const today = dayjs().tz(guessedTimezone).startOf('day');
const defaultEndDay = today.add(DEFAULT_CLASS_DAYS, 'days').endOf('day');

const initialState: ActivityGroupData = {
    is_loading: false,
    error: null,
    sessionValidationErrors: {},
    name: '',
    active: true,
    status: ActivityStatus.Hold,
    start_timestamp: today.unix(),
    end_timestamp: defaultEndDay.unix(),
    selected_timezone: guessedTimezone,
    program_name: '',
    course_name: '',
    catalog_item_id: '',
    catalog_item_versioned_id: '',
    child_activities: [],
    instructor_pool: [],
    delivery_map: {},
    new_delivery_sessions: [],
};

export const createSessionErrorKey = (
    deliveryName: string,
    optionNumber: number,
    sessionId?: string,
): string => {
    const baseKey = `${deliveryName}::${optionNumber}`;
    return sessionId ? `${baseKey}::${sessionId}` : baseKey;
};

export const fetchActivityGroupDetailsOnly = createAsyncThunk<
    ActivityGroupData,
    { name: string; history?: any; navigationPath?: string },
    {
        state: GlobalState;
        dispatch: AppDispatch;
        rejectValue: {
            title: string;
            message: string;
            linkText: string;
        };
    }
>(
    'activityGroup/fetchOnly',
    async ({ name, history, navigationPath }, thunkApi) => {
        try {
            const activityGroup = await getActivityGroupOnly(name);

            if (history && navigationPath) {
                history.push(navigationPath + activityGroup.pk);
            }

            return {
                ...thunkApi.getState().activityGroup,
                ...activityGroup,
                is_loading: false,
            };
        } catch (error) {
            if (error instanceof ApiError && error.statusCode === 404) {
                return thunkApi.rejectWithValue({
                    title: '404 Not Found',
                    message:
                        'The requested activity group could not be found in our system.',
                    linkText: 'Return to Activity Group List',
                });
            }
            throw error;
        }
    },
);

export const fetchActivityGroupByName = createAsyncThunk<
    ActivityGroupData,
    string,
    {
        state: GlobalState;
        dispatch: AppDispatch;
        rejectValue: {
            title: string;
            message: string;
            linkText: string;
        };
    }
>('activityGroup/fetchByName', async (name: string, thunkApi) => {
    let selected_timezone = guessedTimezone;
    try {
        const { activityGroup, childActivities } =
            await getActivityGroupData(name);

        const instructor_pool = childActivities.reduce<DeliveryInstructor[]>(
            (acc, curr) => {
                curr.instructors?.forEach((i) => {
                    const isIncluded = acc.find(
                        (existing) => existing.pk === i.pk,
                    );
                    if (!isIncluded) acc.push(i);
                });

                return acc;
            },
            [],
        );
        const child_activities =
            childActivities.map<ActivityGroupChildActivity>((activity) => {
                selected_timezone =
                    activity.delivery_timezone || guessedTimezone;
                const [_, delivery_name, option_number] =
                    activity.activity_name.split(
                        ACTIVITY_GROUP_NAME_EMBEDDED_VALUE_DELIMITER,
                    );

                return {
                    ...activity,
                    delivery_name,
                    option_number: parseInt(option_number),
                };
            });

        const delivery_map = child_activities.reduce((acc, curr) => {
            if (curr.delivery_name in acc) {
                acc[curr.delivery_name].push(curr.activity_name);
            } else {
                acc[curr.delivery_name] = [curr.activity_name];
            }

            return acc;
        }, {});

        for (const key of Object.keys(delivery_map)) {
            delivery_map[key].sort((a, b) => {
                const aNum = a.split(
                    ACTIVITY_GROUP_NAME_EMBEDDED_VALUE_DELIMITER,
                )[2];
                const bNum = b.split(
                    ACTIVITY_GROUP_NAME_EMBEDDED_VALUE_DELIMITER,
                )[2];

                return aNum - bNum;
            });
        }

        return {
            ...thunkApi.getState().activityGroup,
            ...activityGroup,
            is_loading: false,
            child_activities,
            delivery_map,
            instructor_pool,
            selected_timezone,
        };
    } catch (error) {
        if (error instanceof ApiError && error.statusCode === 404) {
            return thunkApi.rejectWithValue({
                title: '404 Not Found',
                message:
                    'The requested activity group could not be found in our system.',
                linkText: 'Return to Activity Group List',
            });
        }
        throw error;
    }
});

export const activityGroupSlice = createSlice({
    name: 'activityGroup',
    initialState,
    reducers: {
        setSessionValidationError: (
            state,
            action: PayloadAction<{
                deliveryName: string;
                optionNumber: number;
                sessionId: string;
                error: string;
            }>,
        ) => {
            const { deliveryName, optionNumber, sessionId, error } =
                action.payload;
            const errorKey = createSessionErrorKey(
                deliveryName,
                optionNumber,
                sessionId,
            );

            state.sessionValidationErrors[errorKey] = error;
        },
        setActivityGroup: (_, action: PayloadAction<ActivityGroupData>) => {
            return action.payload;
        },
        clearActivityGroup: () => {
            return initialState;
        },
        setLoading: (state, action: PayloadAction<boolean>) => {
            return { ...state, is_loading: action.payload };
        },
        setTimezone: (state, action: PayloadAction<string>) => {
            return {
                ...state,
                selected_timezone: action.payload,
            };
        },
        setActivityGroupStatus: (
            state,
            action: PayloadAction<ActivityStatus>,
        ) => {
            return {
                ...state,
                status: action.payload,
                child_activities: state.child_activities.map((child) => ({
                    ...child,
                    activity_status:
                        child.activity_status === ActivityStatus.Canceled
                            ? child.activity_status
                            : action.payload,
                })),
            };
        },
        setCourse: (
            state,
            action: PayloadAction<
                Pick<
                    ActivityGroupData,
                    | 'catalog_item_id'
                    | 'catalog_item_versioned_id'
                    | 'course_name'
                >
            >,
        ) => {
            return { ...state, ...action.payload };
        },
        setStartTimeStampFromDatePickerValue: (
            state,
            action: PayloadAction<{ value: string; selectedTimezone: string }>,
        ) => {
            state.start_timestamp = dayjs
                .tz(
                    action.payload.value,
                    DATE_PICKER_VALUE_FORMAT,
                    action.payload.selectedTimezone,
                )
                .unix();
        },
        setEndTimeStampFromDatePickerValue: (
            state,
            action: PayloadAction<{ value: string; selectedTimezone: string }>,
        ) => {
            state.end_timestamp = dayjs
                .tz(
                    action.payload.value,
                    DATE_PICKER_VALUE_FORMAT,
                    action.payload.selectedTimezone,
                )
                .unix();
        },
        addDeliveryName: (state, action: PayloadAction<string>) => {
            state.delivery_map[action.payload] = [];
        },
        addDeliveryOption: (
            state,
            action: PayloadAction<{
                deliveryName: string;
                operationsOwnerName: string;
                operationsOwnerEmail: string;
            }>,
        ) => {
            const mostRecentOptionNumber = state.delivery_map[
                action.payload.deliveryName
            ].length
                ? parseInt(
                      state.delivery_map[action.payload.deliveryName]
                          .at(-1)
                          .split(ACTIVITY_GROUP_NAME_EMBEDDED_VALUE_DELIMITER)
                          .at(-1),
                  )
                : 0;
            const newOptionNumber = mostRecentOptionNumber
                ? mostRecentOptionNumber + 1
                : 1;
            const deliveryOptionActivityName =
                `${state.name}${ACTIVITY_GROUP_NAME_EMBEDDED_VALUE_DELIMITER}` +
                `${action.payload.deliveryName}${ACTIVITY_GROUP_NAME_EMBEDDED_VALUE_DELIMITER}${newOptionNumber}`;

            const [existingChildName] =
                state.delivery_map[action.payload.deliveryName];
            const existingChild =
                existingChildName &&
                state.child_activities.find(
                    (ca) => ca.activity_name === existingChildName,
                );

            state.delivery_map[action.payload.deliveryName].push(
                deliveryOptionActivityName,
            );

            const defaultSessionStart = dayjs
                .unix(state.start_timestamp)
                .startOf('day')
                .set('hours', 9);
            const defaultSessionEnd = defaultSessionStart.set('hours', 17);
            const defaultDeliverySession: DeliverySession = {
                start_timestamp: defaultSessionStart.unix(),
                end_timestamp: defaultSessionEnd.unix(),
                delivery_session_type: AciDeliverySessionType.LABOLOGY,
                instructors: [],
                v_ilt_info: {
                    type: V_ILT_TYPE.PVILT,
                },
            };

            if (existingChild) {
                const { pk, ...copiedAttributes } = existingChild;

                state.child_activities.push({
                    ...copiedAttributes,
                    pk: '',
                    activity_name: deliveryOptionActivityName,
                    activity_group_name: state.name,
                    course_name: state.course_name,
                    option_number: newOptionNumber,
                    delivery_sessions: [defaultDeliverySession],
                    class_size: 0,
                    operations_owner: action.payload.operationsOwnerName,
                    operations_owner_email: action.payload.operationsOwnerEmail,
                });
            } else {
                const activityToCopy =
                    state.child_activities.find(
                        (activity) => activity.delivery_city,
                    ) || {};

                state.child_activities.push({
                    delivery_timezone: '',
                    delivery_city: '',
                    delivery_state: '',
                    delivery_country: '',
                    delivery_region: '',
                    delivery_geo: '',
                    activity_type: '',
                    activity_audience: '',
                    activity_modality: '',
                    customer_support_manager: '',
                    customer_support_manager_email: '',
                    topic_name: '',
                    partner_initiative: '',
                    delivery_language: '',
                    scheduler: '',
                    scheduler_email: '',
                    ...activityToCopy,
                    activity_name: deliveryOptionActivityName,
                    activity_group_name: state.name,
                    activity_status: state.status,
                    class_size: 0,
                    course_catalog_item_id: state.catalog_item_id,
                    course_name: state.course_name,
                    course_days: 0,
                    delivery_sessions: [defaultDeliverySession],
                    instructors: [],
                    lms_type: ActivityLMSType.SkillBuilder,
                    operations_owner: action.payload.operationsOwnerName,
                    operations_owner_email: action.payload.operationsOwnerEmail,
                    pk: '',
                    program: state.program_name,
                    provider: 'AWS',
                    delivery_name: action.payload.deliveryName,
                    option_number: newOptionNumber,
                });
            }
        },
        setDeliveryOption: (
            state,
            action: PayloadAction<{
                deliveryName: string;
                optionNumber: number;
                start_timestamp?: number;
                end_timestamp?: number;
                class_size?: number;
                pk?: string;
            }>,
        ) => {
            const {
                deliveryName,
                optionNumber,
                start_timestamp,
                end_timestamp,
                class_size,
                pk,
            } = action.payload;
            const optionActivity = state.child_activities.find(
                (activity) =>
                    activity.delivery_name === deliveryName &&
                    activity.option_number === optionNumber,
            );
            const filteredActivities = state.child_activities.filter(
                (activity) =>
                    activity.activity_name !== optionActivity.activity_name,
            );

            state.child_activities = [
                ...filteredActivities,
                {
                    ...optionActivity,
                    ...(pk ? { pk } : {}),
                    ...(start_timestamp ? { start_timestamp } : {}),
                    ...(end_timestamp ? { end_timestamp } : {}),
                    ...(typeof class_size !== 'undefined'
                        ? { class_size }
                        : {}),
                },
            ];
        },
        addInstructorToPool: (
            state,
            action: PayloadAction<DeliveryInstructor>,
        ) => {
            const instructorPoolPks = state.instructor_pool.map((i) => i.pk);
            if (!instructorPoolPks.includes(action.payload.pk)) {
                state.instructor_pool.push({
                    ...action.payload,
                    name: action.payload.name,
                    type: action.payload.type,
                    location: action.payload.location,
                    role: null,
                    do_not_shuffle: false,
                });
            }
        },
        removeInstructorFromPool: (
            state,
            action: PayloadAction<{ pk: string }>,
        ) => {
            state.instructor_pool = state.instructor_pool.filter(
                (instructor) => instructor.pk !== action.payload.pk,
            );
        },
        addInstructorToDeliverySession: (
            state,
            action: PayloadAction<{
                deliveryName: string;
                optionNumber: number;
                instructor: DeliveryInstructor;
                deliverySessionId: string;
                role?: string;
            }>,
        ) => {
            const {
                deliveryName,
                optionNumber,
                instructor,
                deliverySessionId,
                role = 'Primary',
            } = action.payload;

            const sessionActivity = state.child_activities.find((activity) => {
                return (
                    activity.delivery_name === deliveryName &&
                    activity.option_number === optionNumber
                );
            });
            const targetSessionIndex =
                sessionActivity.delivery_sessions.findIndex(
                    (ds) => ds.id === deliverySessionId,
                );

            const instructorInActivity = !!sessionActivity.instructors.find(
                (i) => i.pk === instructor.pk,
            );
            if (!instructorInActivity) {
                sessionActivity.instructors.push({
                    pk: instructor.pk,
                    name: instructor.name,
                    email: instructor.email,
                    type: instructor.type,
                    location: instructor.location,
                    role: role,
                    do_not_shuffle: false,
                });
            }

            const instructorInDeliverySession =
                !!sessionActivity.delivery_sessions[
                    targetSessionIndex
                ].instructors.find(
                    (i) => i.pk === action.payload.instructor.pk,
                );
            if (!instructorInDeliverySession) {
                sessionActivity.delivery_sessions[
                    targetSessionIndex
                ].instructors.push({
                    ...action.payload.instructor,
                    role: role,
                });
            }
        },
        updateInstructorRole: (
            state,
            action: PayloadAction<{
                deliveryName: string;
                optionNumber: number;
                instructorPk: string;
                deliverySessionId: string;
                role: string;
            }>,
        ) => {
            const {
                deliveryName,
                optionNumber,
                instructorPk,
                deliverySessionId,
                role,
            } = action.payload;

            const sessionActivity = state.child_activities.find(
                (a) =>
                    a.delivery_name === deliveryName &&
                    a.option_number === optionNumber,
            );

            if (!sessionActivity) {
                return;
            }

            const activityInstructor = sessionActivity.instructors.find(
                (i) => i.pk === instructorPk,
            );
            if (activityInstructor) {
                activityInstructor.role = role;
            }

            const targetSessionIndex =
                sessionActivity.delivery_sessions.findIndex(
                    (ds) => ds.id === deliverySessionId,
                );

            if (targetSessionIndex !== -1) {
                const sessionInstructor = sessionActivity.delivery_sessions[
                    targetSessionIndex
                ]?.instructors.find((i) => i.pk === instructorPk);
                if (sessionInstructor) {
                    sessionInstructor.role = role;
                }
            }
        },
        removeInstructorFromDeliverySession: (
            state,
            action: PayloadAction<{
                deliveryName: string;
                optionNumber: number;
                instructorPk: string;
                deliverySessionId: string;
            }>,
        ) => {
            const {
                deliveryName,
                optionNumber,
                instructorPk,
                deliverySessionId,
            } = action.payload;
            const sessionActivity = state.child_activities.find(
                (a) =>
                    a.delivery_name === deliveryName &&
                    a.option_number === optionNumber,
            );
            const targetSessionIndex =
                sessionActivity.delivery_sessions.findIndex(
                    (ds) => ds.id === deliverySessionId,
                );
            sessionActivity.delivery_sessions[targetSessionIndex].instructors =
                sessionActivity.delivery_sessions[
                    targetSessionIndex
                ].instructors.filter((i) => i.pk !== instructorPk);
            const stillAssignedToSessions =
                !!sessionActivity.delivery_sessions.find((ds) =>
                    ds.instructors.map((i) => i.pk).includes(instructorPk),
                );
            if (!stillAssignedToSessions) {
                sessionActivity.instructors =
                    sessionActivity.instructors.filter(
                        (i) => i.pk !== instructorPk,
                    );
            }
        },
        setActivityType: (state, action: PayloadAction<string>) => {
            state.child_activities = state.child_activities.map((activity) => ({
                ...activity,
                activity_type: action.payload,
            }));
        },
        setActivityAudience: (state, action: PayloadAction<string>) => {
            state.child_activities = state.child_activities.map((activity) => ({
                ...activity,
                activity_audience: action.payload,
            }));
        },
        setActivityModality: (state, action: PayloadAction<string>) => {
            state.child_activities = state.child_activities.map((activity) => ({
                ...activity,
                activity_modality: action.payload,
            }));
        },
        setActivityLocation: (
            state,
            action: PayloadAction<{
                timezone: string;
                country: string;
                state: string;
                city: string;
                region: string;
                geo: string;
            }>,
        ) => {
            const oldTimezone = state.selected_timezone;
            const newTimezone = action.payload.timezone;

            state.selected_timezone = newTimezone;

            state.child_activities = state.child_activities.map((activity) => {
                const updatedActivity = {
                    ...activity,
                    delivery_timezone: action.payload.timezone,
                    delivery_country: action.payload.country,
                    delivery_state: action.payload.state,
                    delivery_city: action.payload.city,
                    delivery_region: action.payload.region,
                    delivery_geo: action.payload.geo,
                };

                if (
                    activity.delivery_sessions &&
                    activity.delivery_sessions.length > 0
                ) {
                    updatedActivity.delivery_sessions =
                        activity.delivery_sessions.map((session) => {
                            // DST-aware timezone conversion: preserve local time components
                            // instead of using date formatting which loses timezone context
                            const oldStart = dayjs
                                .unix(session.start_timestamp)
                                .tz(oldTimezone);
                            const oldEnd = dayjs
                                .unix(session.end_timestamp)
                                .tz(oldTimezone);

                            // Extract date and time components separately to handle DST correctly
                            const startYear = oldStart.year();
                            const startMonth = oldStart.month();
                            const startDate = oldStart.date();
                            const startHour = oldStart.hour();
                            const startMinute = oldStart.minute();
                            const startSecond = oldStart.second();

                            const endYear = oldEnd.year();
                            const endMonth = oldEnd.month();
                            const endDate = oldEnd.date();
                            const endHour = oldEnd.hour();
                            const endMinute = oldEnd.minute();
                            const endSecond = oldEnd.second();

                            // Reconstruct in new timezone preserving exact local time
                            const newStart = dayjs()
                                .tz(newTimezone)
                                .year(startYear)
                                .month(startMonth)
                                .date(startDate)
                                .hour(startHour)
                                .minute(startMinute)
                                .second(startSecond);

                            const newEnd = dayjs()
                                .tz(newTimezone)
                                .year(endYear)
                                .month(endMonth)
                                .date(endDate)
                                .hour(endHour)
                                .minute(endMinute)
                                .second(endSecond);

                            return {
                                ...session,
                                start_timestamp: newStart.unix(),
                                end_timestamp: newEnd.unix(),
                            };
                        });
                }

                return updatedActivity;
            });
        },
        addDeliverySession: (
            state,
            action: PayloadAction<{
                deliveryName: string;
                optionNumber: number;
                deliverySession: DeliverySession;
            }>,
        ) => {
            const activity = state.child_activities.find(
                (activity) =>
                    activity.delivery_name === action.payload.deliveryName &&
                    activity.option_number === action.payload.optionNumber,
            );

            activity.delivery_sessions.push({
                ...action.payload.deliverySession,
                id:
                    action.payload.deliverySession.id ||
                    `${activity.pk}-${activity.delivery_sessions.length + 1}`,
                is_deleted: false,
            });
            state.new_delivery_sessions.push({
                id: activity.delivery_sessions.at(-1).id,
                is_new: true,
            });
        },
        setDeliverySession: (
            state,
            action: PayloadAction<{
                deliveryName: string;
                optionNumber: number;
                deliverySession: DeliverySession;
            }>,
        ) => {
            state.child_activities = state.child_activities.map((activity) => {
                if (
                    activity.delivery_name === action.payload.deliveryName &&
                    activity.option_number === action.payload.optionNumber
                ) {
                    const targetIndex = activity.delivery_sessions.findIndex(
                        (session) =>
                            session.id === action.payload.deliverySession.id,
                    );

                    activity.delivery_sessions[targetIndex] =
                        action.payload.deliverySession;
                }

                return activity;
            });
        },
        removeDeliverySession: (
            state,
            action: PayloadAction<{
                deliveryName: string;
                optionNumber: number;
                id: string;
            }>,
        ) => {
            state.child_activities = state.child_activities.map((activity) => {
                if (
                    activity.delivery_name === action.payload.deliveryName &&
                    activity.option_number === action.payload.optionNumber
                ) {
                    activity.delivery_sessions =
                        activity.delivery_sessions.filter(
                            (session) => session.id !== action.payload.id,
                        );
                    state.new_delivery_sessions =
                        state?.new_delivery_sessions?.filter(
                            (session) => session.id !== action.payload.id,
                        );
                }

                return activity;
            });
        },
        clearDeliverySessions: (
            state,
            action: PayloadAction<{
                deliveryName: string;
                optionNumber: number;
            }>,
        ) => {
            state.child_activities = state.child_activities.map((activity) => {
                if (
                    activity.delivery_name === action.payload.deliveryName &&
                    activity.option_number === action.payload.optionNumber
                ) {
                    activity.delivery_sessions = [];
                }

                return activity;
            });
        },
        removeDelivery: (state, action: PayloadAction<string>) => {
            const activitiesToCancel = state.child_activities.filter(
                (activity) => activity.delivery_name === action.payload,
            );
            const cancelledActivities = activitiesToCancel.map((activity) => ({
                ...activity,
                activity_status: ActivityStatus.Canceled,
            }));

            state.child_activities = state.child_activities
                .filter((activity) => activity.delivery_name !== action.payload)
                .concat(cancelledActivities);
            delete state.delivery_map[action.payload];
        },
        clearSessionValidationErrors: (
            state,
            action: PayloadAction<{
                deliveryName?: string;
                optionNumber?: number;
                id?: string;
            }>,
        ) => {
            const { deliveryName, optionNumber, id } = action.payload;
            const errorKey = createSessionErrorKey(
                deliveryName,
                optionNumber,
                id,
            );

            if (deliveryName && optionNumber && id) {
                delete state.sessionValidationErrors[errorKey];
                return;
            }

            if (deliveryName && optionNumber) {
                Object.keys(state.sessionValidationErrors).forEach((key) => {
                    if (key.startsWith(errorKey)) {
                        delete state.sessionValidationErrors[key];
                    }
                });
                return;
            }
        },
        removeDeliveryOption: (
            state,
            action: PayloadAction<{
                deliveryName: string;
                optionNumber: number;
            }>,
        ) => {
            const activityToCancel = state.child_activities.find(
                (activity) =>
                    activity.delivery_name === action.payload.deliveryName &&
                    activity.option_number === action.payload.optionNumber,
            );

            if (activityToCancel.pk) {
                activityToCancel.activity_status = ActivityStatus.Canceled;
            } else {
                state.child_activities = state.child_activities.filter(
                    (activity) => activity !== activityToCancel,
                );
            }

            state.delivery_map[action.payload.deliveryName] =
                state.delivery_map[action.payload.deliveryName].filter(
                    (option) => option !== activityToCancel.activity_name,
                );
        },
        cloneDeliveryOption: (
            state,
            action: PayloadAction<{
                deliveryName: string;
                optionNumberToClone: number;
                operationsOwnerName: string;
                operationsOwnerEmail: string;
            }>,
        ) => {
            const activityToClone = state.child_activities.find(
                (activity) =>
                    activity.delivery_name === action.payload.deliveryName &&
                    activity.option_number ===
                        action.payload.optionNumberToClone,
            );
            const newOptionNumber =
                state.delivery_map[action.payload.deliveryName].length + 1;
            const newActivityName =
                `${state.name}${ACTIVITY_GROUP_NAME_EMBEDDED_VALUE_DELIMITER}` +
                `${action.payload.deliveryName}${ACTIVITY_GROUP_NAME_EMBEDDED_VALUE_DELIMITER}${newOptionNumber}`;

            state.child_activities.push({
                ...activityToClone,
                pk: null,
                activity_name: newActivityName,
                option_number: newOptionNumber,
                operations_owner: action.payload.operationsOwnerName,
                operations_owner_email: action.payload.operationsOwnerEmail,
                delivery_sessions: activityToClone.delivery_sessions.map(
                    (session, index) => {
                        const { id, ...copiedSession } = session;
                        return {
                            ...copiedSession,
                            id: `-${index + 1}`,
                        };
                    },
                ),
            });

            state.delivery_map[action.payload.deliveryName].push(
                newActivityName,
            );
        },
    },
    extraReducers: (builder) => {
        builder
            .addMatcher(
                (action) =>
                    [
                        fetchActivityGroupDetailsOnly.pending.type,
                        fetchActivityGroupByName.pending.type,
                    ].includes(action.type),
                (state) => {
                    state.is_loading = true;
                    state.error = null;
                },
            )
            .addMatcher(
                (action) =>
                    [
                        fetchActivityGroupDetailsOnly.fulfilled.type,
                        fetchActivityGroupByName.fulfilled.type,
                    ].includes(action.type),
                (state, action) => {
                    return {
                        ...state,
                        ...action.payload,
                        is_loading: false,
                        error: null,
                    };
                },
            )
            .addMatcher(
                (action) =>
                    [
                        fetchActivityGroupDetailsOnly.rejected.type,
                        fetchActivityGroupByName.rejected.type,
                    ].includes(action.type),
                (state, action) => {
                    state.is_loading = false;
                    state.error = action.payload;
                },
            );
    },
});

export const addDeliveryOptionWithUser = (deliveryName: string) => {
    return (dispatch: AppDispatch, getState: () => GlobalState) => {
        const state = getState();
        const userProfile = selectUser(state);
        dispatch(
            activityGroupSlice.actions.addDeliveryOption({
                deliveryName: deliveryName,
                operationsOwnerName: userProfile?.profile?.full_name || '',
                operationsOwnerEmail: userProfile?.profile?.email || '',
            }),
        );
    };
};

export const cloneDeliveryOptionWithUser = (
    deliveryName: string,
    optionNumberToClone: number,
) => {
    return (dispatch: AppDispatch, getState: () => GlobalState) => {
        const state = getState();
        const userProfile = selectUser(state);
        dispatch(
            activityGroupSlice.actions.cloneDeliveryOption({
                deliveryName: deliveryName,
                optionNumberToClone,
                operationsOwnerName: userProfile?.profile?.full_name || '',
                operationsOwnerEmail: userProfile?.profile?.email || '',
            }),
        );
    };
};

export const selectSessionError = (state, params) => {
    const errorKey = createSessionErrorKey(
        params.deliveryName,
        params.optionNumber,
        params.sessionId,
    );
    return state.activityGroup.sessionValidationErrors[errorKey];
};

export const selectHasValidationErrors = (state: {
    activityGroup: ActivityGroupData;
}) => Object.keys(state.activityGroup.sessionValidationErrors).length > 0;

export const selectActivityGroupLoading = (state: {
    activityGroup: ActivityGroupData;
}) => state.activityGroup.is_loading;
export const selectActivityGroupError = (state: {
    activityGroup: ActivityGroupData;
}) => state.activityGroup.error;

export const {
    setActivityLocation,
    setActivityGroup,
    clearActivityGroup,
    setLoading,
} = activityGroupSlice.actions;

export const removeDeliverySessionWithValidationClear =
    (params: {
        deliveryName: string;
        optionNumber: number;
        id: string;
        index?: number;
    }) =>
    (dispatch: AppDispatch) => {
        dispatch(
            activityGroupSlice.actions.clearSessionValidationErrors({
                ...params,
                id:
                    params.id ||
                    (params.index !== undefined ? `${params.index + 1}` : null),
            }),
        );
        dispatch(activityGroupSlice.actions.removeDeliverySession(params));
    };

export const removeDeliveryOptionWithValidationClear =
    (params: { deliveryName: string; optionNumber: number }) =>
    (dispatch: AppDispatch) => {
        dispatch(
            activityGroupSlice.actions.clearSessionValidationErrors({
                deliveryName: params.deliveryName,
                optionNumber: params.optionNumber,
            }),
        );
        dispatch(activityGroupSlice.actions.removeDeliveryOption(params));
    };

export default activityGroupSlice.reducer;
export const { setSessionValidationError } = activityGroupSlice.actions;
